import { Capacitor, registerPlugin } from '@capacitor/core';

/**
 * Small, privacy-safe boundary for the Android Quick Athena overlay.
 *
 * The native surface receives visual state plus, only while its compact card
 * is open, one bounded message and one bounded response in memory. It never
 * receives an access token, account identifier, session/history, microphone
 * data, or a URL supplied by the web layer.
 * Browser callers get an explicit unavailable result instead of a simulated
 * floating button.
 */
const NativeAthenaFloatingOverlay = registerPlugin('AthenaFloatingOverlay');

const PREFERENCE_KEY = 'athena_floating_overlay_enabled_v1';
const INTERACTION_LEVEL_KEY = 'athena_floating_overlay_interaction_level_v1';
const INTERACTION_LEVELS = new Set(['low', 'medium', 'high']);
const VISUAL_STATES = new Set([
  'idle', 'listening', 'speaking', 'thinking', 'idea', 'happy',
  'caring', 'reminder', 'focus', 'sleepy', 'alert', 'offline',
]);
const NOOP = () => {};

const UNAVAILABLE_STATUS = Object.freeze({
  available: false,
  configured: false,
  enabled: false,
  visible: false,
  running: false,
  userHidden: false,
  activityVisible: true,
  permission: 'unavailable',
  permissionGranted: false,
  visualState: 'offline',
  interactionLevel: 'medium',
});

function isNativeAndroid() {
  try {
    return Capacitor.isNativePlatform() && Capacitor.getPlatform() === 'android';
  } catch {
    return false;
  }
}

function pluginAvailable() {
  try {
    return isNativeAndroid() && Capacitor.isPluginAvailable('AthenaFloatingOverlay');
  } catch {
    return false;
  }
}

function normalizeVisualState(value, fallback = 'idle') {
  const state = typeof value === 'string' ? value.trim().toLowerCase() : '';
  return VISUAL_STATES.has(state) ? state : fallback;
}

function readConfiguredPreference() {
  if (typeof window === 'undefined') return false;
  try {
    return window.localStorage.getItem(PREFERENCE_KEY) === 'true';
  } catch {
    return false;
  }
}

function writeConfiguredPreference(enabled) {
  if (typeof window === 'undefined') return;
  try {
    if (enabled === true) window.localStorage.setItem(PREFERENCE_KEY, 'true');
    else window.localStorage.removeItem(PREFERENCE_KEY);
  } catch {
    // Storage can be unavailable in a private WebView. The native operation
    // itself remains explicit and is never replaced by a fake preference.
  }
}

function normalizeInteractionLevel(value, fallback = 'medium') {
  const level = typeof value === 'string' ? value.trim().toLowerCase() : '';
  return INTERACTION_LEVELS.has(level) ? level : fallback;
}

function readInteractionLevel() {
  if (typeof window === 'undefined') return 'medium';
  try {
    return normalizeInteractionLevel(window.localStorage.getItem(INTERACTION_LEVEL_KEY));
  } catch {
    return 'medium';
  }
}

function writeInteractionLevel(value) {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(INTERACTION_LEVEL_KEY, normalizeInteractionLevel(value));
  } catch {
    // The native bridge remains the source of truth when WebView storage is
    // unavailable; do not manufacture a successful configuration state.
  }
}

function normalizePermission(value, fallback = 'unknown') {
  if (value === true) return 'granted';
  const permission = typeof value === 'string' ? value.trim().toLowerCase() : '';
  if (['granted', 'allowed', 'authorized'].includes(permission)) return 'granted';
  if (['denied', 'blocked', 'not_granted'].includes(permission)) return 'denied';
  if (['not_requested', 'prompt', 'unknown'].includes(permission)) return permission;
  return fallback;
}

function normalizeStatus(raw, configured = readConfiguredPreference()) {
  const source = raw && typeof raw === 'object' ? raw : {};
  const permission = normalizePermission(
    source.permission ?? source.overlayPermission ?? source.canDrawOverlays ?? source.granted,
  );
  const permissionGranted = source.permissionGranted === true
    || source.granted === true
    || source.canDrawOverlays === true
    || permission === 'granted';
  return Object.freeze({
    available: pluginAvailable(),
    configured: configured === true,
    enabled: source.enabled === true || source.serviceEnabled === true || source.running === true,
    visible: source.visible === true || source.overlayVisible === true,
    running: source.running === true || source.serviceRunning === true || source.enabled === true,
    // Dragging the overlay to Android's dismiss target is an intentional
    // user action. Keep it distinct from a transient foreground hide so the
    // lifecycle sync never recreates it behind the person's back.
    userHidden: source.userHidden === true || source.dismissed === true,
    activityVisible: source.activityVisible !== false,
    permission: permissionGranted ? 'granted' : permission,
    permissionGranted,
    visualState: normalizeVisualState(source.visualState ?? source.state, permissionGranted ? 'idle' : 'offline'),
    interactionLevel: normalizeInteractionLevel(
      source.interactionLevel ?? source.interaction_level,
      readInteractionLevel(),
    ),
  });
}

async function callNative(methods, args = undefined) {
  if (!pluginAvailable()) return { called: false, value: null };
  for (const method of methods) {
    try {
      if (typeof NativeAthenaFloatingOverlay?.[method] !== 'function') continue;
      const value = args === undefined
        ? await NativeAthenaFloatingOverlay[method]()
        : await NativeAthenaFloatingOverlay[method](args);
      return { called: true, value };
    } catch {
      // Older APKs and a user-revoked special permission are normal runtime
      // conditions. Return a transparent status rather than throwing from a
      // lifecycle event or pretending the overlay is active.
    }
  }
  return { called: false, value: null };
}

async function hideNativeOverlay() {
  return callNative(['hide', 'hideOverlay', 'stopOverlay']);
}

/** Returns true only in an Android APK that includes the native plugin. */
export function isAthenaFloatingOverlayAvailable() {
  return pluginAvailable();
}

/** Reads the explicit per-installation toggle; it never requests permission. */
export function isAthenaFloatingOverlayConfigured() {
  return readConfiguredPreference();
}

/**
 * Reads current native state. The web/PWA fallback is deliberately marked as
 * unavailable rather than mirroring a pretend status.
 */
export async function getAthenaFloatingOverlayStatus() {
  if (!pluginAvailable()) return UNAVAILABLE_STATUS;
  const result = await callNative(['getStatus', 'getOverlayStatus', 'status']);
  return normalizeStatus(result.value);
}

/**
 * Opens Android's special overlay-access flow only after a direct user action.
 * Callers must not use this from app boot, lifecycle, a notification, or an
 * assistant response.
 */
export async function requestAthenaFloatingOverlayPermission() {
  if (!pluginAvailable()) return UNAVAILABLE_STATUS;
  const result = await callNative([
    'requestPermission',
    'requestOverlayPermission',
    'openOverlayPermissionSettings',
  ]);
  const status = normalizeStatus(result.value);
  // Android settings can return before the user toggles the grant. Re-read
  // current status whenever the plugin exposes it, without assuming success.
  const current = await getAthenaFloatingOverlayStatus();
  return current.permission !== 'unknown' || current.permissionGranted || result.called
    ? current
    : status;
}

/**
 * Starts the configured native overlay service. Permission is never requested
 * implicitly; the caller must invoke requestAthenaFloatingOverlayPermission
 * first and observe a granted result.
 */
export async function enableAthenaFloatingOverlay({ visualState = 'idle' } = {}) {
  if (!pluginAvailable()) return UNAVAILABLE_STATUS;
  const before = await getAthenaFloatingOverlayStatus();
  if (!before.permissionGranted) return before;

  const result = await callNative(['setEnabled', 'enable', 'start'], {
    enabled: true,
    visualState: normalizeVisualState(visualState),
  });
  const nativeStatus = normalizeStatus(result.value, result.called);
  if (result.called && (nativeStatus.enabled || nativeStatus.running || nativeStatus.permissionGranted)) {
    writeConfiguredPreference(true);
  }
  // Prefer a real post-action read. A native plugin can return only `{ ok }`
  // from setEnabled, while getStatus is the authoritative source of whether
  // the foreground service actually started.
  const current = await getAthenaFloatingOverlayStatus();
  return current.enabled || current.running || !result.called
    ? current
    : normalizeStatus(result.value, readConfiguredPreference());
}

/** Hides/stops the overlay and clears its explicit local configuration. */
export async function disableAthenaFloatingOverlay({ clearPreference = true } = {}) {
  if (clearPreference) writeConfiguredPreference(false);
  if (!pluginAvailable()) return UNAVAILABLE_STATUS;

  await hideNativeOverlay();
  const result = await callNative(['setEnabled', 'disable', 'stop'], { enabled: false });
  return normalizeStatus(result.value, readConfiguredPreference());
}

/** Updates only the compact visual state; it never starts voice or chat. */
export async function setAthenaFloatingOverlayVisualState(visualState = 'idle', intensity = 0.55) {
  const state = normalizeVisualState(visualState);
  if (!pluginAvailable()) return { ...UNAVAILABLE_STATUS, visualState: 'offline' };
  const numericIntensity = Number.isFinite(Number(intensity))
    ? Math.max(0, Math.min(1, Number(intensity)))
    : 0.55;
  const result = await callNative(['setVisualState', 'setState', 'updateVisualState'], {
    visualState: state,
    intensity: numericIntensity,
  });
  return normalizeStatus(result.value, readConfiguredPreference());
}

/**
 * Controls only the bubble's visual reactivity. `low` is still, `medium`
 * pulses for active states, and `high` is more responsive. It does not create
 * check-ins, notifications, messages, or other proactive behavior.
 */
export async function setAthenaFloatingOverlayInteractionLevel(level = 'medium') {
  const interactionLevel = normalizeInteractionLevel(level);
  if (!pluginAvailable()) return { ...UNAVAILABLE_STATUS, interactionLevel };

  const result = await callNative([
    'setInteractionLevel',
    'setProactivityLevel',
    'setInteractionPreference',
  ], { interactionLevel, level: interactionLevel });

  if (result.called) writeInteractionLevel(interactionLevel);
  const current = await getAthenaFloatingOverlayStatus();
  return result.called
    ? { ...current, interactionLevel: current.interactionLevel || interactionLevel }
    : { ...current, interactionLevel };
}

/**
 * Lifecycle reconciler used by NativeAppShell.  It is safe to call often:
 * foreground always hides the overlay; background shows it only when the
 * authenticated person previously enabled it through the explicit settings
 * path. This method never opens Android settings or auto-enables the feature.
 */
export async function syncAthenaFloatingOverlay({
  authenticated = false,
  appState = 'foreground',
  visualState = 'idle',
} = {}) {
  if (!pluginAvailable()) return UNAVAILABLE_STATUS;

  const status = await getAthenaFloatingOverlayStatus();
  const shouldShow = authenticated === true
    && appState === 'background'
    && status.configured === true
    && status.enabled === true
    && status.userHidden !== true
    && status.permissionGranted === true;

  if (!shouldShow) {
    await hideNativeOverlay();
    return getAthenaFloatingOverlayStatus();
  }

  await setAthenaFloatingOverlayVisualState(visualState);
  const shown = await callNative(['show', 'showOverlay', 'startOverlay'], {
    visualState: normalizeVisualState(visualState),
  });
  return normalizeStatus(shown.value, true);
}

/**
 * Logout/account-isolation boundary. There is no account or chat data in the
 * native plugin, nevertheless we clear the opt-in so another account cannot
 * inherit a visible floating assistant by accident.
 */
export async function clearAthenaFloatingOverlayRuntime() {
  return disableAthenaFloatingOverlay({ clearPreference: true });
}

/**
 * Sends a React-pipeline result to one currently open native card. Nothing is
 * persisted by this function or the Android plugin; a stale request ID is
 * rejected instead of being delivered to a later card.
 */
export async function deliverAthenaFloatingOverlayQuickResult({
  requestId,
  success = false,
  response = '',
} = {}) {
  const safeRequestId = typeof requestId === 'string' ? requestId.trim() : '';
  if (!/^[A-Za-z0-9-]{16,96}$/.test(safeRequestId) || !pluginAvailable()) return false;
  const safeResponse = String(response ?? '')
    .replace(/\u0000/g, ' ')
    .trim()
    .slice(0, 6000);
  const result = await callNative(['deliverQuickResult'], {
    requestId: safeRequestId,
    status: success === true ? 'success' : 'error',
    response: safeResponse,
  });
  return result.called === true && result.value?.delivered === true;
}

/**
 * Optional native event stream. Status events stay state-only. The sole
 * exception is `quick_submit`: its bounded text/request ID are ephemeral and
 * are intentionally exposed only to the authenticated root bridge.
 */
export async function observeAthenaFloatingOverlay(listener) {
  if (!pluginAvailable() || typeof listener !== 'function') return NOOP;
  try {
    const handle = await NativeAthenaFloatingOverlay.addListener('overlayEvent', (event) => {
      const type = typeof event?.type === 'string' ? event.type.trim().toLowerCase() : 'status';
      if (type === 'quick_submit') {
        const requestId = typeof event?.requestId === 'string' ? event.requestId.trim() : '';
        const text = typeof event?.text === 'string' ? event.text.trim() : '';
        if (!/^[A-Za-z0-9-]{16,96}$/.test(requestId) || !text || text.length > 2000) return;
        listener(Object.freeze({ type, requestId, text }));
        return;
      }
      listener(Object.freeze({
        type: ['opened', 'closed', 'dismissed', 'status'].includes(type) ? type : 'status',
        status: normalizeStatus(event),
      }));
    });
    return () => { try { void handle?.remove?.(); } catch { /* noop */ } };
  } catch {
    return NOOP;
  }
}
